import type { App } from '@hangar/shared';
import { AppPageLink } from './AppPageLink';
import { buildsOnClass, buildsOnHeader, buildsOnId, buildsOnLabel, buildsOnTitle, bundleIdTitle } from './appsTableModel';
import { useSeesProviders } from '@/lib/useAudience';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import type { RowAction } from '@/components/RowActions';
import { FRAMEWORK_LABEL } from '@/components/badges';

interface Props {
  apps: App[];
  /** For the link to each App page (`/projects/:slug/apps/:app`). */
  projectSlug: string;
  /** The bundle id its profiles check (`verification.bundleId`), when one does. */
  bundleIds: Record<string, string | undefined>;
  selectedId: string | undefined;
  onSelect: (app: App) => void;
  actionsFor: (app: App) => RowAction[];
}

/** The name picks the app this page is about; the arrow next to it opens the app's own page. */
function AppNameCell({ app, projectSlug, selectedId, onSelect }: Pick<Props, 'projectSlug' | 'selectedId' | 'onSelect'> & { app: App }) {
  return (
    <span className="flex min-w-0 max-w-full items-start gap-1">
      <button type="button" data-testid={`gs-app-select-${app.slug}`} aria-pressed={app.id === selectedId} onClick={() => onSelect(app)} className="flex min-w-0 flex-col rounded-sm text-left">
        <span className="truncate text-[13px] font-medium text-foreground underline-offset-4 hover:underline" title={app.name}>
          {app.name}
        </span>
        <span className="truncate font-mono text-xs text-subtle">{app.slug}</span>
      </button>
      <AppPageLink app={app} projectSlug={projectSlug} testId={`gs-app-open-${app.slug}`} />
    </span>
  );
}

function RuntimeCell({ app, bundleId }: { app: App; bundleId: string | undefined }) {
  const sees = useSeesProviders();
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-[13px] text-foreground">{FRAMEWORK_LABEL[app.framework]}</span>
      <span className="truncate font-mono text-xs text-subtle" title={bundleIdTitle(bundleId)}>
        {bundleId ?? '—'}
      </span>
      {/* Narrow cards fold the build setup column into this cell. */}
      <span className="hidden truncate font-mono text-xs text-subtle @max-[40rem]:block" title={buildsOnId(app, sees)}>
        {buildsOnLabel(app, sees)}
      </span>
    </span>
  );
}

function BuildsOnCell({ app }: { app: App }) {
  const sees = useSeesProviders();
  return (
    <span className={buildsOnClass(app)} title={buildsOnTitle(app, sees)}>
      {buildsOnLabel(app, sees)}
    </span>
  );
}

function WorkingDirCell({ app }: { app: App }) {
  return (
    <span className="block truncate font-mono text-xs text-muted-foreground" title={app.workingDirectory}>
      {app.workingDirectory}
    </span>
  );
}

function PhoneAppRow({ app, bundleId, ...name }: Pick<Props, 'projectSlug' | 'selectedId' | 'onSelect'> & { app: App; bundleId: string | undefined }) {
  const sees = useSeesProviders();
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <AppNameCell app={app} {...name} />
      <span className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-xs text-subtle">
        <span>{FRAMEWORK_LABEL[app.framework]}</span>
        <span className="max-w-full truncate">{app.workingDirectory}</span>
        {bundleId ? <span className="max-w-full truncate">{bundleId}</span> : null}
        <span>{buildsOnLabel(app, sees)}</span>
      </span>
    </span>
  );
}

function appColumns({ projectSlug, bundleIds, selectedId, onSelect }: Props, sees: boolean): CompactColumn<App>[] {
  return [
    { key: 'app', header: 'App', cell: (a) => <AppNameCell app={a} projectSlug={projectSlug} selectedId={selectedId} onSelect={onSelect} />, className: 'max-w-0', width: 'w-[30%]' },
    { key: 'runtime', header: 'Runtime & bundle id', cell: (a) => <RuntimeCell app={a} bundleId={bundleIds[a.id]} />, className: 'max-w-0', width: 'w-[30%]', hide: '@max-[30rem]:hidden' },
    { key: 'dir', header: 'Working dir', cell: (a) => <WorkingDirCell app={a} />, className: 'max-w-0' },
    { key: 'eas', header: buildsOnHeader(sees), cell: (a) => <BuildsOnCell app={a} />, hide: '@max-[40rem]:hidden' },
  ];
}

/** Step 3's table: app (select), framework and bundle id, working directory, its build setup (operators: the EAS project or Bitrise app), Edit · Archive. */
export function AppsTable(props: Props) {
  const sees = useSeesProviders();
  const { apps, projectSlug, bundleIds, selectedId, onSelect, actionsFor } = props;
  return (
    <CompactTable
      label="Apps"
      rows={apps}
      columns={appColumns(props, sees)}
      rowKey={(a) => a.id}
      rowTestId={(a) => `gs-app-${a.slug}`}
      subject={(a) => a.name}
      actions={actionsFor}
      selected={(a) => a.id === selectedId}
      phoneRow={(a) => <PhoneAppRow app={a} bundleId={bundleIds[a.id]} projectSlug={projectSlug} selectedId={selectedId} onSelect={onSelect} />}
    />
  );
}
